"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa6";

import { useContactPopup } from "@/components/shared/ContactPopupProvider";
import { hashHref } from "@/lib/hash-href";

const accent = "text-[#ff5c00]";

const SOCIAL_LINKS = [
  { label: "Hi Ho on Facebook", href: "https://www.facebook.com/hihodigitalmarketing", Icon: FaFacebookF },
  { label: "Hi Ho on Instagram", href: "https://www.instagram.com/hihodigital/", Icon: FaInstagram },
  { label: "Hi Ho on LinkedIn", href: "https://www.linkedin.com/company/hiho-creating-websites/", Icon: FaLinkedinIn },
] as const;

const serviceLinks = [
  { label: "Concept and Design", href: "/concept-and-design" },
  { label: "Web Design & Development", href: "/website-design" },
  { label: "Digital Marketing", href: "/digital-marketing" },
] as const;

export function Footer() {
  const pathname = usePathname();
  const { openContact } = useContactPopup();

  return (
    <footer
      className="border-t border-[#231f20]/10 bg-[#ddd5cc] font-sans text-[#231f20]"
      aria-label="Site footer"
    >
      {/* CTA */}
      <div
        id="contact"
        className="site-content scroll-mt-20 pt-16 pb-12 md:scroll-mt-24 md:py-16 lg:py-20"
      >
        <p className="text-sm font-medium tracking-wide text-[#231f20]/80 md:text-base">
          Contact us
        </p>
        <h2 className="mt-6 max-w-[min(100%,90rem)] font-medium text-5xl leading-[1em] sm:leading-[0.90] tracking-tight sm:text-5xl md:mt-4 md:text-6xl lg:text-9xl xl:text-[13em]">
          <Link
            href={hashHref(pathname, "contact")}
            className="block text-black transition-opacity hover:opacity-80 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#ff5c00] md:inline-block"
            onClick={(e) => {
              e.preventDefault();
              openContact();
            }}
          >
            <span className="inline">
              Want to create with <span className={accent}>Hi Ho</span>
            </span>
            <span
              aria-hidden
              className="ml-3 inline-flex shrink-0 items-center justify-center rounded-full bg-white p-2.5 sm:mt-6 sm:p-4 md:ml-2 md:mt-0 md:align-middle md:p-6 lg:p-8 xl:p-10"
            >
              <svg
                className="size-5 sm:size-6 md:size-8 lg:size-9 xl:size-10"
                viewBox="0 0 44 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M2.93332 17.1429L34.8015 17.1429L21.7684 4.87698C20.7336 3.76125 20.7336 1.95263 21.7684 0.836893C22.8031 -0.278842 24.8526 -0.278842 25.8873 0.836895L43.0577 18.1341L43.2584 18.1957C44.0294 19.3178 43.9582 21.0165 43.0577 21.9535L25.8873 39.1631C24.8526 40.2788 22.8031 40.2788 21.7684 39.1631C20.7336 38.0474 20.7336 36.2387 21.7684 35.123L34.8015 22.8571L2.93332 22.8571C1.31444 22.8571 1.34062e-06 21.578 1.41429e-06 20C1.48572e-06 18.422 1.31444 17.1429 2.93332 17.1429Z"
                  fill="black"
                />
              </svg>
            </span>
          </Link>
        </h2>
      </div>

      <div className="h-px w-full bg-[#231f20]/20" aria-hidden />

      {/* Brand + services */}
      <div className="site-content py-12 md:py-14 lg:py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 xl:gap-24">
          <div>
            <Link
              href="/"
              className="inline-block focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#ff5c00]"
            >
              <Image
                src="/images/hiho-logo-footer.svg"
                alt="HiHo"
                width={810}
                height={418}
                className="h-14 w-auto sm:h-16 md:h-30"
              />
            </Link>
            <ul className="mt-8 flex flex-wrap gap-4 sm:gap-6" role="list">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <li key={label}>
                  <Link
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="inline-flex size-10 items-center justify-center rounded-full bg-[#231f20] text-white transition-colors hover:bg-[#231f20]/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff5c00] sm:size-12"
                  >
                    <Icon className="size-[18px] sm:size-5" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className={`text-sm font-semibold uppercase tracking-wide ${accent} sm:text-2xl`}>
              What we do
            </h3>
            <ul className="mt-6 space-y-3 text-base text-[#231f20]/50 sm:mt-8 sm:text-lg md:text-3xl">
              {serviceLinks.map((item) => (
                <li key={item.label}>
                  <Link
                    href={item.href}
                    className="transition-colors hover:text-[#231f20] focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff5c00]"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="h-px w-full bg-[#231f20]/20" aria-hidden />

      {/* Legal */}
      <div className="site-content py-6 md:py-8">
        <div className="flex flex-col items-center gap-4 text-xs text-[#231f20]/75 sm:text-lg md:flex-row md:justify-between md:gap-6">
          <Link
            href="/terms"
            className="order-2 hover:text-[#231f20] focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff5c00] md:order-1"
          >
            Terms and conditions
          </Link>
          <p className="order-1 text-center md:order-2">
            Copyright 2026 © Hi Ho
          </p>
          <Link
            href="/privacy"
            className="order-3 hover:text-[#231f20] focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff5c00]"
          >
            Privacy policy 
          </Link>
        </div>
      </div>
    </footer>
  );
}
